import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { recordRecentPath } from '../hooks/useRecentPaths';

/**
 * ScrollToTop — mounted inside BrowserRouter. Runs once on every pathname
 * change and scrolls the window + any scrollable root container to the top.
 *
 * Intentionally skipped:
 *   - POP transitions (browser back/forward) — browsers already restore scroll.
 *   - Hash-only changes (#anchor) — the target anchor handles its own scroll.
 *   - Same-path re-renders — handled by the pathname dependency.
 *
 * No behaviour changes for modals/drawers/tabs (they don't change `pathname`).
 */
export default function ScrollToTop() {
  const { pathname, hash, search } = useLocation();
  const navigationType = useNavigationType(); // 'POP' | 'PUSH' | 'REPLACE'

  // Record this visit for the "Recent" section in the profile menu —
  // skipped for hash-only changes / browser back so we only capture real
  // forward navigation.
  useEffect(() => {
    if (navigationType === 'POP') return;
    recordRecentPath(`${pathname}${search || ''}`);
  }, [pathname, search, navigationType]);

  useEffect(() => {
    // Back/forward — let the browser restore the prior scroll position.
    if (navigationType === 'POP') return;
    // Anchor navigation — let the #id handle it.
    if (hash) return;

    // Use instant scroll, not smooth — users expect immediate top on nav.
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      // Extra safety: some layouts have an inner scrollable main element.
      const main = document.querySelector('main');
      if (main && typeof main.scrollTo === 'function') {
        main.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      }
    }
  }, [pathname, hash, navigationType]);

  return null;
}
